/**
 * UpdateBuilderForm Component
 * UI-komponent för att uppdatera befintligt byggföretag
 * Endast rendering + event handlers - all logik i useBuilderForm hook
 */

import React, { useState } from 'react';
import { BuilderFormFields } from './BuilderFormFields';
import { YrkesgruppPricingSection } from './YrkesgruppPricingSection';
import { CertificationsSection } from './CertificationsSection';
import BuilderSubcontractorsTab from './BuilderSubcontractorsTab';
import { useBuilderForm } from '../hooks/useBuilderForm';
import FormInput from '../../../components/common/FormInput';

function UpdateBuilderForm({ builder, setBuilders, builders, users = [], onClose, onSuccess }) {
  const [activeTab, setActiveTab] = useState('info'); // 'info' eller 'pricing'
  
  const {
    values,
    setValues,
    previewUrl,
    errors,
    isLoading,
    errorMessage,
    connectedUsers,
    handleChange,
    handleAvatarChange,
    handleRemoveAvatar,
    handleSetContactPerson,
    handleSetPuPriorityContact,
    handleSubmit
  } = useBuilderForm({
    initialBuilder: builder,
    users,
    onClose,
    onSuccess,
    setBuilders,
    builders,
    isUpdate: true
  });

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">Uppdatera byggföretag</h2>
      {errorMessage && <p className="text-red-500 mb-4">{errorMessage}</p>}
      
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`${
              activeTab === 'info'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Företagsinformation
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`${
              activeTab === 'pricing'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Priser per yrkesgrupp
          </button>
          <button
            onClick={() => setActiveTab('insurance')}
            className={`${
              activeTab === 'insurance'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Behörigheter & försäkring
          </button>
          <button
            onClick={() => setActiveTab('subcontractors')}
            className={`${
              activeTab === 'subcontractors'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Underleverantörer
          </button>
        </nav>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Tab content */}
        {activeTab === 'info' && (
          <BuilderFormFields
            values={values}
            setValues={setValues}
            errors={errors}
            previewUrl={previewUrl}
            handleChange={handleChange}
            handleAvatarChange={handleAvatarChange}
            handleRemoveAvatar={handleRemoveAvatar}
            isUpdate={true}
            connectedUsers={connectedUsers}
            onSetContactPerson={handleSetContactPerson}
            onSetPuPriorityContact={handleSetPuPriorityContact}
            showYrkesgruppPricing={false}
          />
        )}

        {activeTab === 'pricing' && (
          <div className="space-y-6">
            {/* Prissättning för offerter */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Prissättning för offerter</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Löptimme minimum (SEK)"
                  type="number"
                  name="hourlyRateMin"
                  value={values.hourlyRateMin}
                  onChange={(e) => setValues(prev => ({ ...prev, hourlyRateMin: Number(e.target.value) }))}
                  min="0"
                  step="10"
                  placeholder="650"
                  required
                />

                <FormInput
                  label="Löptimme maximum (SEK)"
                  type="number"
                  name="hourlyRateMax"
                  value={values.hourlyRateMax}
                  onChange={(e) => setValues(prev => ({ ...prev, hourlyRateMax: Number(e.target.value) }))}
                  min="0"
                  step="10"
                  placeholder="890"
                  required
                />
              </div>

              <FormInput
                label="Uppbärningskostnad (SEK/h)"
                type="number"
                name="materialHandlingRate"
                value={values.materialHandlingRate}
                onChange={(e) => setValues(prev => ({ ...prev, materialHandlingRate: Number(e.target.value) }))}
                min="0"
                step="10"
                placeholder="570"
                required
                className="mt-4"
              />

              <div className="text-sm text-gray-600 mt-3">
                <p>• <strong>Löptimme spann:</strong> Används för att visa prisintervall i offerter (t.ex. "650-890 SEK/h")</p>
                <p>• <strong>Uppbärningskostnad:</strong> Timkostnad för materialhantering och transport (SEK/h)</p>
              </div>
            </div>

            {/* Yrkesgruppspriser */}
            <YrkesgruppPricingSection
              values={values}
              setValues={setValues}
              isCollapsible={false}
            />
          </div>
        )}

        {activeTab === 'insurance' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Försäkring</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Försäkringsbolag
                  </label>
                  <input
                    type="text"
                    name="insuranceCompany"
                    value={values.insuranceCompany || ''}
                    onChange={handleChange}
                    placeholder="T.ex. Länsförsäkringar, If, Trygg-Hansa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Ange vilket försäkringsbolag företaget har sin allrisk- och ansvarsförsäkring hos
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Behörigheter</h3>
              <CertificationsSection
                certifications={values.certifications || []}
                onChange={(newCertifications) => setValues(prev => ({
                  ...prev,
                  certifications: newCertifications
                }))}
                builderId={builder?._id}
              />
            </div>
          </div>
        )}
        
        {activeTab === 'subcontractors' && (
          <BuilderSubcontractorsTab builderId={builder?._id} />
        )}

        {/* Underleverantörsfliken sparar via sin egen endpoint, så formulärets
            Spara-knapp hör inte dit — den hade sparat byggföretaget i stället
            och fått det att se ut som att kopplingen gick förlorad. */}
        {activeTab !== 'subcontractors' && (
        <div className="flex space-x-4 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-500 text-white p-2 rounded hover:bg-gray-600 
                     transition-colors duration-200"
          >
            Avbryt
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-leadme-pink text-white p-2 rounded hover:bg-leadme-pink-dark 
                     disabled:bg-leadme-pink/50 transition-colors duration-200"
          >
            {isLoading ? 'Sparar...' : 'Spara'}
          </button>
        </div>
        )}
      </form>
    </div>
  );
}

export default UpdateBuilderForm;
